/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { OptionalSuspense } from '@/features/performance/components/OptionalSuspense';
import type { ContentStackBanner } from '../../../features/contentstack/interface';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { memo, useMemo, useEffect, useState } from 'react';
import { Skeleton } from '@az/starc-ui';
import styles from '../styles.module.scss';
import { sessionStorage } from '@/utils/sessionStorage';
import { transformOrdersToPurchases } from '@/utils/transformOrdersToPurchases';
import { useFeatureFlag } from '@/features/globalConfig';
import { useOrderHistory } from '@/features/orderHistory/api/getOrderHistoryData';
import { useBuyAgainPurchases } from '@/features/orderHistory/api/getBuyAgainPurchases';
import { countryCodes } from '@/constants/locale';
import { useLocale } from '@/hooks/useLocale';
import { BuyAgainContainer } from '@/features/orderHistory/components/BuyAgain/BuyAgainContainer';
import { getFormattedDateRange } from '@/features/orderHistory/utils/getFormattedDateRange';
import type { BuyAgainPurchase } from '@/features/orderHistory';
import { usePageType } from '@/hooks/usePageType';
import { pageTypes } from '@/constants/page';
import { shipmentStatusTypes } from '@/constants/shipmentStatusTypes';
import { useAppData } from '@/hooks/useAppData';
import React from 'react';

type BuyAgainSessionStorageKey =
  | 'showHomeBuyAgainCandidates'
  | 'showPLPBuyAgainCandidates'
  | 'showPDPBuyAgainCandidates'
  | 'showAccountBuyAgainCandidates';

type Props = {
  limit: number;
  title: string;
  dateRange: number;
  fallbackBanner?: ContentStackBanner;
  shouldReduceSpacing?: boolean;
  customClass?: string;
  partTerminologyId?: string;
  partGroupId?: string;
  currentSkuId?: string;
  SpacerComponent: React.ComponentType<{
    children: React.ReactNode;
    shouldReduceSpacing?: boolean;
    className?: string;
    removeVerticalSpacing?: boolean;
    removeHorizontalSpacing?: boolean;
    id?: string;
  }>;
  sessionStorageKey: BuyAgainSessionStorageKey;
  isPendingData?: boolean;
};

export const BuyAgainCandidatesWithFallback = memo(function Component({
  limit,
  title,
  dateRange,
  fallbackBanner,
  shouldReduceSpacing,
  customClass,
  partTerminologyId,
  partGroupId,
  currentSkuId,
  SpacerComponent,
  sessionStorageKey,
  isPendingData,
}: Props) {
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  const [showBuyAgainCandidates, setShowBuyAgainCandidates] = useState(userAuthenticated);
  const [isFirstBuyAgainCall, setIsFirstBuyAgainCall] = useState(false);
  const locale = useLocale();
  const isUS = locale === countryCodes.us;
  const buyAgainEndpointEnabled = useFeatureFlag('BUY_AGAIN_ENDPOINT_ENABLED') === 'true';

  const currentDate = new Date();
  const { fromDate, toDate } = getFormattedDateRange({ date: currentDate, dateRange });
  const { data: purchasesData, isPending: purchasesDataPending } = useBuyAgainPurchases({
    fromDate,
    toDate,
    enabled:
      buyAgainEndpointEnabled && isUS && userAuthenticated && showBuyAgainCandidates && limit > 0,
  });

  const { data: ordersHistoryData, isPending: orderHistoryDataPending } = useOrderHistory({
    fromDate,
    toDate,
    orderStatus: 'COMPLETED',
    enabled:
      !buyAgainEndpointEnabled && isUS && userAuthenticated && showBuyAgainCandidates && limit > 0,
  });

  const displayData = useMemo((): BuyAgainPurchase[] => {
    if (buyAgainEndpointEnabled && purchasesData?.purchases) {
      const eligibleItems = purchasesData.purchases.filter(
        (purchase) =>
          (purchase.orderStatus === shipmentStatusTypes.noPendingAction ||
            purchase.orderStatus === shipmentStatusTypes.inStorePicked) &&
          Number(purchase.quantity) - Number(purchase.returnedQuantity) > 0 &&
          (partTerminologyId ? purchase.partTerminologyId === partTerminologyId : true) &&
          (partGroupId ? purchase.partGroupId === partGroupId : true),
      );
      return eligibleItems
        .filter((purchase) => purchase.skuId !== currentSkuId)
        .filter(
          (purchase, index, purchasesArray) =>
            index ===
            purchasesArray.findIndex(
              (purchasedItem) =>
                purchasedItem.skuId === purchase.skuId && !!purchasedItem.detailUrl,
            ),
        );
    }
    if (!buyAgainEndpointEnabled && ordersHistoryData?.orders) {
      return transformOrdersToPurchases(ordersHistoryData.orders);
    }
    return [];
  }, [
    buyAgainEndpointEnabled,
    purchasesData?.purchases,
    ordersHistoryData?.orders,
    partTerminologyId,
    partGroupId,
    currentSkuId,
  ]);

  const isPending = buyAgainEndpointEnabled
    ? purchasesDataPending || isPendingData
    : orderHistoryDataPending;

  useEffect(() => {
    const showBuyAgainCandidates = sessionStorage.getItem(sessionStorageKey) !== 'false';
    setShowBuyAgainCandidates(showBuyAgainCandidates);
  }, [sessionStorageKey]);

  useEffect(() => {
    setIsFirstBuyAgainCall((previous) => (!previous ? purchasesDataPending : previous));
  }, [purchasesDataPending]);

  const handleCloseBuyAgainCandidates = () => {
    sessionStorage.setItem(sessionStorageKey, 'false');
    setShowBuyAgainCandidates(false);
  };

  const { pageType } = usePageType();
  const isAccountPage = pageType === pageTypes.myAccount;
  const isHomePage = pageType === pageTypes.home;

  const shouldDisplayContainer = userAuthenticated && showBuyAgainCandidates && isUS;

  if (buyAgainEndpointEnabled && isFirstBuyAgainCall && !isAccountPage && !isHomePage) {
    return null;
  }

  if (shouldDisplayContainer) {
    if (isPending) {
      return (
        <SpacerComponent>
          <Skeleton
            className={styles.skeleton}
            attributes={{ 'data-testid': 'action-candidates-skeleton' }}
          />
        </SpacerComponent>
      );
    }

    if (displayData && displayData.length > 0) {
      return (
        <OptionalSuspense>
          <SpacerComponent removeVerticalSpacing removeHorizontalSpacing>
            <BuyAgainContainer
              purchases={displayData}
              label={title}
              limit={limit}
              onClose={handleCloseBuyAgainCandidates}
            />
          </SpacerComponent>
        </OptionalSuspense>
      );
    }
  }
  if (fallbackBanner) {
    return (
      <OptionalSuspense>
        <SpacerComponent
          shouldReduceSpacing={shouldReduceSpacing}
          className={customClass}
          {...(fallbackBanner.component_id && {
            id: fallbackBanner.component_id,
          })}
        >
          <CMSBanner content={fallbackBanner} />
        </SpacerComponent>
      </OptionalSuspense>
    );
  }
  return null;
});
